import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import {
  DARK_PALETTES,
  getDarkPalette,
  getThemePreference,
  isDarkNow,
  setDarkPalette,
  setThemePreference,
  subscribeTheme,
  type DarkPalette,
  type ThemePreference,
} from '../lib/theme.ts';
import { AutoThemeIcon, CheckIcon, MoonIcon, SunIcon } from './icons.tsx';
import { cx } from './index.tsx';

const THEMES: Array<{ key: ThemePreference; label: string; icon: typeof SunIcon }> = [
  { key: 'system', label: 'Automático', icon: AutoThemeIcon },
  { key: 'light', label: 'Claro', icon: SunIcon },
  { key: 'dark', label: 'Oscuro', icon: MoonIcon },
];

/**
 * Menú «Apariencia»: tema (automático, claro u oscuro) y colores del modo oscuro. La elección
 * queda guardada en este dispositivo. up: el menú abre hacia arriba (barra lateral, abajo de todo).
 */
export function AppearanceMenu({ withLabel, up, className }: { withLabel?: boolean; up?: boolean; className?: string }) {
  const pref = useSyncExternalStore(subscribeTheme, getThemePreference);
  const palette = useSyncExternalStore(subscribeTheme, getDarkPalette);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();

  // Se cierra tocando afuera o con Escape (y el foco vuelve al botón).
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choosePalette = (key: DarkPalette) => {
    setDarkPalette(key);
    // Elegir colores oscuros viendo la app clara: se pasa a oscuro para que se vea el cambio.
    if (!isDarkNow()) setThemePreference('dark');
  };

  const Current = THEMES.find((t) => t.key === pref)?.icon ?? AutoThemeIcon;

  return (
    <div ref={box} className={cx('relative', className)}>
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={id}
        aria-label="Apariencia: tema claro u oscuro y colores"
        title="Apariencia"
        className="flex w-full items-center gap-3 rounded-lg p-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
      >
        <Current className="size-5" />
        {withLabel && <span>Apariencia</span>}
      </button>
      {open && (
        <div
          id={id}
          role="dialog"
          aria-label="Apariencia"
          className={cx(
            'absolute z-30 w-72 rounded-xl bg-white p-3 text-slate-900 shadow-raised ring-1 ring-slate-200',
            up ? 'bottom-full left-0 mb-2' : 'top-full right-0 mt-2',
          )}
        >
          <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Tema</p>
          <div role="radiogroup" aria-label="Tema" className="mt-2 grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1">
            {THEMES.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={pref === key}
                onClick={() => setThemePreference(key)}
                className={cx(
                  'flex flex-col items-center gap-1 rounded-md py-2 text-xs font-medium transition-colors',
                  pref === key ? 'bg-white text-slate-900 shadow-card' : 'text-slate-600 hover:text-slate-900',
                )}
              >
                <Icon className="size-4" />
                {label}
              </button>
            ))}
          </div>

          <p className="mt-4 text-xs font-semibold tracking-wide text-slate-500 uppercase">Colores del modo oscuro</p>
          <div role="radiogroup" aria-label="Colores del modo oscuro" className="mt-2 grid grid-cols-2 gap-1">
            {DARK_PALETTES.map((p) => {
              const active = palette === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => choosePalette(p.key)}
                  className={cx(
                    'flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
                    active ? 'bg-slate-100 font-medium text-slate-900' : 'text-slate-700 hover:bg-slate-50',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="size-5 shrink-0 rounded-full ring-1 ring-slate-300"
                    style={{ background: `linear-gradient(135deg, ${p.swatch[0]} 50%, ${p.swatch[1]} 50%)` }}
                  />
                  <span className="min-w-0 flex-1 truncate">{p.label}</span>
                  {active && <CheckIcon className="size-4 shrink-0 text-brand" />}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Se guarda en este dispositivo.
            <span className="hidden md:inline"> En modo claro, también tiñe la barra del costado.</span>
          </p>
        </div>
      )}
    </div>
  );
}
